import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  clearPreparedTabAudioCapture,
  prepareTabAudioCapture,
} from '@/audio/preparedTabAudioCapture'
import { useStore } from '@/store/useStore'
import {
  checkMeetingTitle,
  createMeeting as createMeetingOnServer,
  createMeetingShares,
} from '@/services/meetings'
import { ApiError } from '@/services/api'
import { getServiceStrings } from '@/services/i18n'
import { isOfflineSessionKind } from '@/constants/session-kind'
import type { CreateMeetingPayload } from '@/pages/Home/components/CreateMeetingModal'

/**
 * Signals to the modal that the backend rejected the create request with a
 * duplicate-title 409. The modal maps this to the inline error message next
 * to the "Meeting Name" input.
 */
export class MeetingTitleTakenError extends Error {
  /**
   * The meeting holding the name is in the trash, so it is on no list the
   * user can see. Worth a different sentence: the answer is to restore or
   * empty it, not to hunt for a meeting that is not there.
   */
  readonly inTrash: boolean

  constructor(message = 'Meeting title already exists', inTrash = false) {
    super(message)
    this.name = 'MeetingTitleTakenError'
    this.inTrash = inTrash
  }
}

function isMeetingTitleTakenError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 409
}

/** The 409 a soft-deleted meeting raises, as opposed to a listed one. */
function isTitleHeldByTrash(error: ApiError): boolean {
  return error.code === 'MEETING_TITLE_TAKEN_IN_TRASH'
}

/**
 * Preflight duplicate-title check against `GET /api/meetings/title-availability`.
 * Throws {@link MeetingTitleTakenError} when the title is already taken.
 */
export async function assertMeetingTitleAvailable(title: string): Promise<void> {
  try {
    await checkMeetingTitle(title)
  } catch (error) {
    if (isMeetingTitleTakenError(error)) {
      throw new MeetingTitleTakenError(undefined, isTitleHeldByTrash(error))
    }
    throw error
  }
}

/**
 * Drives the "Create Meeting" modal submit flow:
 *   1. Prepare tab-audio capture first when the user opted into system audio
 *      (prompts the browser's share picker) — done before persisting
 *      anything so a cancelled/denied picker never leaves an orphaned
 *      meeting row behind. Duplicate titles are checked in the modal before
 *      this hook runs (see {@link assertMeetingTitleAvailable}).
 *   2. POST /api/meetings to persist the meeting row.
 *   3. Seed the Zustand store with the backend UUID (so the live UI and the
 *      finished-detail page share the same identifier).
 *   4. Navigate to `/meetings/:id/live` (or `/record` for internal meetings).
 *
 * Duplicate titles surface as a 409 `ApiError` from the server; this hook
 * re-throws them as {@link MeetingTitleTakenError} so the modal can render
 * the inline "already taken" message.
 */
export function useCreateMeetingFlow() {
  const navigate = useNavigate()
  const createMeeting = useStore((s) => s.createMeeting)
  const setSettings = useStore((s) => s.setSettings)
  const setShareOnCreateError = useStore((s) => s.setShareOnCreateError)
  const [error, setError] = useState<string | null>(null)

  const startMeeting = useCallback(
    async (payload: CreateMeetingPayload) => {
      setError(null)

      clearPreparedTabAudioCapture()
      if (payload.transcriptionSource === 'browser' && payload.shareTabAudioConfirmed) {
        try {
          await prepareTabAudioCapture(payload.microphoneDeviceId, payload.hasMicrophone)
        } catch {
          throw new Error(getServiceStrings().tabAudioCaptureFailed)
        }
      }

      let saved
      try {
        saved = await createMeetingOnServer({
          title: payload.title,
          sourceLang: payload.sourceLang,
          targetLang: payload.targetLang,
          sttProvider: payload.sttProvider,
          // Recorded on the row rather than counted from clicks: these are
          // properties of the meeting that ran, and a row that already says
          // which languages were used is the right place to ask how each mode
          // is used (BRAI-677).
          mode: payload.mode,
          sessionKind: payload.sessionKind,
          // Omitted rather than sent empty: the server stores "no project" as
          // null, and an empty string would have to be trimmed at both ends.
          ...(payload.backlogProjectKey ? { backlogProjectKey: payload.backlogProjectKey } : {}),
          // Absent unless this meeting shares a room — either because the form
          // picked recipients, or because it was opened from an invite.
          ...(payload.sharedRoomId ? { sharedRoomId: payload.sharedRoomId } : {}),
        })
      } catch (apiError) {
        if (isMeetingTitleTakenError(apiError)) {
          clearPreparedTabAudioCapture()
          throw new MeetingTitleTakenError(undefined, isTitleHeldByTrash(apiError))
        }
        throw apiError
      }

      createMeeting({
        id: saved.id,
        title: saved.title,
        sourceLang: saved.sourceLang,
        targetLang: saved.targetLang,
        sttProvider: saved.sttProvider,
        sessionKind: payload.sessionKind,
        context: payload.context,
        // Read back from the server rather than echoed from the payload, so the
        // live screen declares the room the row actually carries.
        sharedRoomId: saved.sharedRoomId ?? null,
        // Likewise, and it matters more here: the server trims the key and
        // stores a blank one as null, so echoing the form's value would put a
        // whitespace project on the wire that the row does not have.
        backlogProjectKey: saved.backlogProjectKey ?? null,
      })

      setSettings({
        sourceLang: payload.sourceLang,
        targetLang: payload.targetLang,
        audioSource: payload.shareTabAudioConfirmed ? 'system' : 'mic',
        context: payload.context,
        meetingSetup: {
          mode: payload.mode,
          sessionKind: payload.sessionKind,
          microphoneDeviceId: payload.microphoneDeviceId,
          microphoneLabel: payload.microphoneLabel,
          shareTabAudio: payload.shareTabAudioConfirmed,
          sendScreenFrames: payload.sendScreenFrames,
          hasMicrophone: payload.hasMicrophone,
          glossaryId: payload.glossaryId,
          transcriptionSource: payload.transcriptionSource,
          nativeMeetingReference: payload.nativeMeetingReference,
        },
        pendingStartMeetingId: saved.id,
      })

      // Share before navigating so the recipients' banners are already up by
      // the time the sharer reaches the live screen. Deliberately not fatal:
      // the meeting exists and the user asked to start it, so a failed share
      // must not strand them in the modal with a title that now 409s. The
      // message is parked in the store instead, which survives this navigate.
      setShareOnCreateError(null)
      if (payload.shareWithUserIds.length > 0) {
        try {
          await createMeetingShares(saved.id, { userIds: payload.shareWithUserIds })
        } catch (shareError) {
          if (import.meta.env.DEV) console.error('Failed to share meeting', shareError)
          setShareOnCreateError(getServiceStrings().shareOnCreateFailed)
        }
      }

      const livePath = saved.sharedRoomId
        ? `/meetings/${saved.id}/live?room=${encodeURIComponent(saved.sharedRoomId)}`
        : `/meetings/${saved.id}/live`
      navigate(
        isOfflineSessionKind(payload.sessionKind)
          ? `/meetings/${saved.id}/record`
          : livePath
      )
    },
    [createMeeting, navigate, setSettings, setShareOnCreateError]
  )

  return { startMeeting, error, setError }
}
